import type { ProvedorOpencode } from '@remoto/protocolo';
import type { UsoDaSessao } from '@remoto/shared';
import { porcento, tokensCurtos } from '../../formato';
import { usarUsoDasSessoes } from '../../uso/consultas';
import {
  contextoEmTexto,
  contextoGrande,
  custoEmTexto,
  descreverAssinaturaDaSessao,
  explicacaoDoCustoDaSessao,
  rotuloDoCustoDoOpencode,
} from '../../uso/cota';
import { BarraDeCota } from '../MedidorDeCota';
import { SeloAssinatura } from '../SeloAssinatura';
import { IconeAviso } from '../icones';
import { BotaoLeve } from '../layout';

/**
 * O uso da sessão no cabeçalho da conversa, estilo `/status`: quanto da
 * semana ela gastou e quanto contexto carrega.
 *
 * O contexto é o número que decide o custo da PRÓXIMA mensagem: cada uma relê
 * a conversa inteira, e acima de 150 mil é o que mais pesa na cota — foi o que
 * a medição de 2026-09-23 achou na mestre (500 a 680 mil, reaquecidos a cada
 * pausa longa). O aviso aparece ali e oferece o gesto que resolve.
 *
 * **Duas peças, porque o telefone não tem linha sobrando.** O cabeçalho no
 * celular já ocupa um quinto da tela, e a conversa tem de ficar com a maior
 * parte dela (`celular.spec.ts`, que mordeu a primeira versão: o bloco inteiro
 * custava ~80 px e a conversa caiu de 57% para 48%). Abaixo de `lg` — o mesmo
 * 1024 px em que o `Shell` troca para as abas — fica só o `ResumoDoUso`, na
 * fileira do título, que não custa linha nenhuma; tocar nele abre o
 * `DetalheDoUso`. No desktop o detalhe está sempre aberto e o resumo some.
 *
 * Nada aparece enquanto a API não devolve esta sessão: fora do escopo, API
 * antiga sem a rota, ou máquina que nunca mediu. Um "0%" ali seria afirmação;
 * ausência não é.
 */

function usoDaSessao(sessaoId: string): UsoDaSessao | undefined {
  return usarUsoDasSessoes([sessaoId])[sessaoId];
}

/** A frase inteira, para o leitor de tela e para o `title` do resumo. */
function descricao(uso: UsoDaSessao): string {
  const semana =
    uso.pctSemanal === null
      ? 'semana sem dado'
      : `${porcento(uso.pctSemanal)} da semana` + (uso.email ? ` da conta ${uso.email}` : '');
  const contexto = contextoEmTexto(uso.contextoTokens, uso.contextoLimite);
  return (
    `Uso desta sessão: ${semana}` +
    (contexto ? `, contexto ${contexto}` : '') +
    (contextoGrande(uso.contextoTokens) ? ' — contexto grande' : '')
  );
}

/** O selo da fileira do título, só abaixo de `lg`. Âmbar e com ícone quando o contexto é grande. */
export function ResumoDoUso({
  sessaoId,
  aberto,
  aoAlternar,
}: {
  sessaoId: string;
  aberto: boolean;
  aoAlternar: () => void;
}): React.JSX.Element | null {
  const uso = usoDaSessao(sessaoId);
  if (!uso) return null;
  const grande = contextoGrande(uso.contextoTokens);

  return (
    <button
      type="button"
      data-testid="uso-resumo"
      onClick={aoAlternar}
      aria-expanded={aberto}
      aria-label={descricao(uso) + (aberto ? ' — esconder detalhes' : ' — ver detalhes')}
      title={descricao(uso)}
      className={
        'flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs tabular-nums lg:hidden ' +
        'pointer-coarse:min-h-9 ' +
        (grande
          ? 'border-amber-900/60 bg-amber-950/30 text-amber-200'
          : 'border-zinc-800 text-zinc-400')
      }
    >
      {grande ? <IconeAviso tamanho={12} /> : null}
      {uso.pctSemanal === null ? 'sem dado' : porcento(uso.pctSemanal)}
      {grande && uso.contextoTokens !== null ? ` · ${tokensCurtos(uso.contextoTokens)}` : ''}
    </button>
  );
}

/** A linha estilo `/status` e o aviso de contexto grande. Sempre no desktop; no celular, quando aberto. */
export function DetalheDoUso({
  sessaoId,
  aberto,
  aoCompactar,
  compactando = false,
  provedorOpencode = null,
}: {
  sessaoId: string;
  aberto: boolean;
  /** Só quando a sessão aceita `/compact` agora — em observada ele não chega ao CLI. */
  aoCompactar?: () => void;
  compactando?: boolean;
  /**
   * O provedor de modelos da conversa, quando ela é do OpenCode (F2). Com ele,
   * a linha ganha o custo da sessão com o nome certo — "equivalente" no
   * NanoGPT, "custo real" no OpenRouter (D45) — e o selo "assinatura".
   * Ausente nas conversas de Claude e Codex, que têm o % da semana.
   */
  provedorOpencode?: ProvedorOpencode | null;
}): React.JSX.Element | null {
  const uso = usoDaSessao(sessaoId);
  if (!uso) return null;
  const contexto = contextoEmTexto(uso.contextoTokens, uso.contextoLimite);
  const custo = provedorOpencode ? custoEmTexto(uso.custoEquivUsd) : null;

  return (
    <div className={aberto ? '' : 'hidden lg:block'}>
      <p
        data-testid="uso-da-sessao"
        className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500"
      >
        <span
          className="flex items-center gap-1.5"
          title={
            'Estimativa por rateio: o % semanal da conta' +
            (uso.email ? ` ${uso.email}` : '') +
            ' dividido pelo peso desta sessão na semana'
          }
        >
          <BarraDeCota utilizacao={uso.pctSemanal} rotulo="Parte da semana desta sessão" className="w-12" />
          {uso.pctSemanal === null ? 'semana: sem dado' : `${porcento(uso.pctSemanal)} da semana`}
        </span>
        {contexto ? <span>contexto {contexto}</span> : null}
        {custo && provedorOpencode ? (
          <span
            data-testid="custo-da-sessao"
            className="flex items-center gap-1"
            title={
              descreverAssinaturaDaSessao(uso) ?? explicacaoDoCustoDaSessao(provedorOpencode)
            }
          >
            {rotuloDoCustoDoOpencode(provedorOpencode)} {custo}
            <SeloAssinatura assinatura={uso.assinatura} />
          </span>
        ) : null}
      </p>
      {contextoGrande(uso.contextoTokens) ? (
        <div
          role="status"
          data-testid="aviso-contexto-grande"
          className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-amber-900/60 bg-amber-950/30 px-2 py-1 text-xs text-amber-200"
        >
          <IconeAviso tamanho={13} />
          <span className="min-w-0 flex-1">
            contexto grande: cada mensagem relê tudo — compacte ou comece outra sessão
          </span>
          {aoCompactar ? (
            <BotaoLeve onClick={aoCompactar} disabled={compactando} title="Envia /compact para esta sessão">
              {compactando ? 'enviando…' : 'Compactar'}
            </BotaoLeve>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
